import type { SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type ParamValue = string | string[] | number | null;

export const useQueryParam = <T extends ParamValue>(paramName: string, defaultValue: T): [T, (value: T) => void] => {
  const [paramValue, setParamValue] = useState<T | null>(defaultValue);
  const refDefaultValue = useRef(defaultValue);
  refDefaultValue.current = defaultValue;

  // if defaultValue is array, then make sure hook returns array type to caller
  const castToDefaultValueType = useCallback(
    (value: ParamValue) => (Array.isArray(defaultValue) && typeof value === 'string' ? value.split(',') : value),
    [defaultValue],
  );

  const refCastToDefaultValueType = useRef(castToDefaultValueType);
  refCastToDefaultValueType.current = castToDefaultValueType;

  const set = useCallback(
    (value: ParamValue) => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const actualValue = Array.isArray(value) ? value.join(',') : value;
      if (actualValue != null && actualValue !== undefined && actualValue !== 'null') {
        urlSearchParams.set(paramName, actualValue as string);
      } else {
        urlSearchParams.delete(paramName);
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${urlSearchParams}`);
      setParamValue(value as SetStateAction<T>);
    },
    [paramName],
  );

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const value = urlSearchParams.get(paramName) || refDefaultValue.current;

    set(value === 'null' ? null : refCastToDefaultValueType.current(value));

    const handlePopstate = () => {
      const updatedValue = urlSearchParams.get(paramName) || refDefaultValue.current;
      setParamValue(value === 'null' ? null : (refCastToDefaultValueType.current(updatedValue) as SetStateAction<T>));
    };

    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [paramName, set]);

  return [paramValue as T, set];
};
